import type { Ingredient } from '../types/inventory';

export type IngredientSortKey = 'folio' | 'name' | 'category' | 'unit' | 'presentations' | 'status';

export interface IngredientSort {
  key: IngredientSortKey;
  dir: 'asc' | 'desc';
}

/**
 * Orden del catálogo de insumos para la vista Lista. Extraída como función pura (en vez de dejarla
 * dentro del componente) para poder probar la regla sin renderizar — mismo criterio que el resto de
 * utils de este paquete.
 *
 * El código IN- se asigna al dar de alta y NO sigue al nombre, así que en una lista alfabética se ve
 * salteado (IN-0008, IN-0002, IN-0009...). Por eso la lista es ordenable por columna en vez de tener
 * un solo orden fijo.
 *
 * sensitivity 'base' => "Nescafe" y "Nescafé" ordenan juntos (el catálogo real tiene nombres con y
 * sin acento); numeric => "Vaso 2" antes que "Vaso 10" y no al revés.
 */
const collator = new Intl.Collator('es', { sensitivity: 'base', numeric: true });

/** Agrupa por estado dejando al final lo que ya no se usa a diario. */
export function ingredientStatusRank(value: Ingredient): number {
  if (value.approvalStatus === 'PENDING_APPROVAL') return 2;
  if (value.approvalStatus === 'REJECTED') return 3;
  return value.isActive ? 0 : 1;
}

function compareBy(a: Ingredient, b: Ingredient, key: IngredientSortKey): number {
  switch (key) {
    // El folio viene formateado con ceros a la izquierda (IN-0007), así que compararlo como texto da
    // el mismo resultado que compararlo como número.
    case 'folio': return collator.compare(a.folio ?? '', b.folio ?? '');
    case 'category': return collator.compare(a.category || '', b.category || '');
    case 'unit': return collator.compare(a.baseUnit.symbol, b.baseUnit.symbol);
    case 'presentations': return a.presentations.length - b.presentations.length;
    case 'status': return ingredientStatusRank(a) - ingredientStatusRank(b);
    default: return collator.compare(a.name, b.name);
  }
}

/** Devuelve una copia ordenada: no muta el arreglo que viene del API. */
export function sortIngredients(ingredients: Ingredient[], sort: IngredientSort): Ingredient[] {
  return [...ingredients].sort((a, b) => {
    // Un insumo sin código va al final en LAS DOS direcciones: es dato ausente, no un valor bajo.
    if (sort.key === 'folio' && !a.folio !== !b.folio) return a.folio ? -1 : 1;
    const result = compareBy(a, b, sort.key);
    // Desempate por nombre: hace el orden estable y deja alfabético el interior de cada grupo cuando
    // ordenas por categoría, unidad o estado (columnas que repiten valor en muchas filas).
    return (sort.dir === 'asc' ? result : -result) || collator.compare(a.name, b.name);
  });
}

/** Siguiente estado al hacer clic en un encabezado: misma columna alterna dirección, otra arranca asc. */
export function nextIngredientSort(current: IngredientSort, key: IngredientSortKey): IngredientSort {
  return { key, dir: current.key === key && current.dir === 'asc' ? 'desc' : 'asc' };
}

/**
 * Orden con el que ABRE la vista Lista. Por código y no por nombre: el dueño renumeró el catálogo a
 * mano (Helado, Cono, los vasos, luego el resto) justo para que ese fuera el orden que ve al entrar,
 * y arrancar por nombre lo obligaba a dar clic en "Código" cada vez.
 * Pendiente: recordar la última elección por usuario — docs/FUTURE_UPGRADES.md §7.
 */
export const DEFAULT_INGREDIENT_SORT: IngredientSort = { key: 'folio', dir: 'asc' };
